<script lang="ts" setup>
import { computed, ref } from 'vue'

import { opChain } from '@collector/shared-utils'
import {
  CricketSubParticipantStatId,
  EventsLineupsParticipant,
  Participant,
} from '@collector/sportsapi-client-legacy'
import UpdateEventSubParticipants from '@mobile/ActionQueue/Actions/Event/UpdateEventSubParticipants'
import { EventSettingsType } from '@mobile/database/EventSettingsRepository'
import { getTeamSide } from '@mobile/reusables/entityUtils'
import { useEventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'
import SelectPlayerModal from '@mobile/views/Relation/Shared/SelectPlayer/SelectPlayerModal.vue'
import { useEventSettings } from '@mobile/views/Relation/useEventSettings'

import { useCricketCurrentBattersAndBowler } from '../../useCricketCurrentBattersAndBowler'
import LiveViewSubParticipantStats from './LiveViewSubParticipantStats.vue'
import { prepareUpdateEventSubParticipantsPayload } from './utils'

interface Props {
  type: 'main' | 'sub'
  dir: 'ltr' | 'rtl'
  bowler?: boolean
  batter?: boolean
  participant: Participant
}

const props = defineProps<Props>()

// composable
const { probableEvent, probableHomeLineups, probableAwayLineups, actionQueue } =
  useEventInjections()
const { updateSetting } = useEventSettings(EventSettingsType.MOBILE)
const { currentMainBatter, currentSecondaryBatter, currentBowler } =
  useCricketCurrentBattersAndBowler(
    ref(getTeamSide(probableEvent.value.participants, props.participant.id)),
  )

// data
const side = getTeamSide(probableEvent.value.participants, props.participant.id)

const currentParticipantLineups =
  side === 'home' ? probableHomeLineups : probableAwayLineups

// state
const isModalOpen = ref(false)

// computed
const currentSelectedPlayer = computed(() => {
  if (props.type === 'main') {
    if (props.batter) {
      return currentMainBatter.value
    } else {
      return currentBowler.value
    }
  } else {
    return currentSecondaryBatter.value
  }
})

// methods
function toggleModal(): void {
  isModalOpen.value = !isModalOpen.value
}

function handleSelectPlayerModalSave(
  participant: EventsLineupsParticipant,
): void {
  switch (props.type) {
    case 'main':
      addUpdateEventSubParticipantAction(participant)
      break
    case 'sub':
      updateSetting('currentSelectedCachedBatterId', participant.participant_id)
      break
  }
}

function addUpdateEventSubParticipantAction(
  participant: EventsLineupsParticipant,
): void {
  const relevantStatId = props.batter
    ? CricketSubParticipantStatId.CurrentBatter
    : CricketSubParticipantStatId.CurrentBowler

  actionQueue.value.add(
    new UpdateEventSubParticipants(
      prepareUpdateEventSubParticipantsPayload(
        relevantStatId,
        participant.participant_id,
        currentParticipantLineups.value,
      ),
    ),
  )
}
</script>

<template>
  <SelectPlayerModal
    v-if="isModalOpen"
    :players="currentParticipantLineups"
    :selectedPlayerId="
      opChain(currentSelectedPlayer, ({ participant_id }) => participant_id) ||
      undefined
    "
    @close="isModalOpen = false"
    @save="handleSelectPlayerModalSave"
  />
  <div
    class="group flex w-full to-white font-bold"
    :class="{
      'flex-row-reverse': dir === 'rtl',
      'bg-gradient-to-r': dir === 'ltr',
      'bg-gradient-to-l': dir === 'rtl',
      'from-home': side === 'home' && type === 'main',
      'from-home/50': side === 'home' && type === 'sub',
      'from-away/75': side === 'away' && type === 'main',
      'from-away/50': side === 'away' && type === 'sub',
    }"
  >
    <div
      class="w-2"
      :class="{ 'bg-home': side === 'home', 'bg-away': side === 'away' }"
    ></div>
    <div
      class="flex w-full justify-between px-3 py-2"
      :class="{ 'flex-row-reverse': dir === 'rtl' }"
    >
      <div
        class="flex-1"
        :class="{ 'text-right': dir === 'rtl' }"
      >
        <div v-if="type === 'main' && batter">CURRENT BATTER</div>
        <div
          class="flex gap-2"
          :class="{ 'flex-row-reverse': dir === 'rtl' }"
        >
          <div
            v-if="!currentSelectedPlayer"
            class="text-gray/50"
          >
            <span v-if="batter && type === 'main'">Select current batter</span>
            <span v-if="batter && type === 'sub'">Select second batter</span>
            <span v-if="bowler && type === 'main'">Select current bowler</span>
          </div>
          <div
            v-else
            class="flex gap-4"
          >
            <div v-if="currentSelectedPlayer.participant_shirt_number">
              {{ currentSelectedPlayer.participant_shirt_number }}
            </div>
            <div>{{ currentSelectedPlayer.participant_name }}</div>
            <LiveViewSubParticipantStats
              :batter
              :bowler
              :subParticipantId="currentSelectedPlayer.participant_id!"
            />
          </div>
        </div>
      </div>
      <div class="grid h-full content-center">
        <button
          class="hidden group-hover:block"
          @click="toggleModal"
        >
          <FontAwesomeIcon icon="edit" />
        </button>
      </div>
    </div>
  </div>
</template>
